import React, { useState } from 'react';
import { motion, Variants, AnimatePresence } from 'framer-motion';
import { FaApple, FaShieldAlt, FaChartLine, FaRegHandshake, FaMicrophoneAlt, FaCommentDots, FaVolumeUp, FaPhoneAlt } from 'react-icons/fa';

// Placeholder images - replace with your actual images for iOS
import heroBgImage from '../../assets/img.png';
import iphoneFeature1 from '../../assets/iphone/phone6.png';
import iphoneFeature2 from '../../assets/features/phone1.png';
import iphoneFeature3 from '../../assets/features/phone2.png';
import iphoneFeature4 from '../../assets/features/phone3.png';
import iphoneFeature5 from '../../assets/features/phone4.png';
import iphoneFeature6 from '../../assets/features/spam-caller.png';
import screenshot1 from '../../assets/ComboPhone.png';
import screenshot2 from '../../assets/iphone/phone6.png';
import screenshot3 from '../../assets/features/phone2.png';
import screenshot4 from '../../assets/iphone/phone5.png';
import videoplay from '../../assets/Demo2.mp4'; // Replace with your video file path

// Data for the interactive features
const interactiveFeatures = [
    {
        id: 1,
        title: "Siri Integration",
        description: "Use Siri to manage your earnings, check call history, and enable features with simple voice commands.",
        image: iphoneFeature1,
        alignment: "left"
    },
    {
        id: 2,
        title: "iMessage Sync",
        description: "Our app seamlessly integrates with iMessage to provide real-time updates on your earnings and call activity.",
        image: iphoneFeature2,
        alignment: "left"
    },
    {
        id: 3,
        title: "Apple Pay Payouts",
        description: "Effortlessly transfer your earnings to your Apple Pay account for secure and instant access.",
        image: iphoneFeature3,
        alignment: "left"
    },
    {
        id: 4,
        title: "Face ID Protection",
        description: "Secure your app and earnings with Face ID. Only you can access your dashboard and sensitive information.",
        image: iphoneFeature4,
        alignment: "right"
    },
    {
        id: 5,
        title: "App Clips & Shortcuts",
        description: "Quickly access key features like call blocking or analytics without fully opening the app, right from your home screen.",
        image: iphoneFeature5,
        alignment: "right"
    },
    {
        id: 6,
        title: "CallKit Integration",
        description: "Our features are seamlessly integrated into the native iOS call screen, providing a familiar and smooth user experience.",
        image: iphoneFeature6,
        alignment: "right"
    }
];

const IphonePage = () => {
    const [selectedFeature, setSelectedFeature] = useState(interactiveFeatures[0]);

    // Framer Motion variants
    const heroVariants: Variants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                staggerChildren: 0.3
            }
        }
    };
    
    const itemVariants: Variants = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0 }
    };
    
    const blinkVariants: Variants = {
        blinking: {
            opacity: [0.2, 1, 0.2],
            transition: {
                duration: 1.5,
                repeat: Infinity,
                repeatType: "loop",
            }
        }
    };
    
    const imageVariants: Variants = {
        initial: { opacity: 0, scale: 0.8, rotateY: 10 },
        animate: { opacity: 1, scale: 1, rotateY: 0, transition: { duration: 0.5, ease: "easeOut" } },
        exit: { opacity: 0, scale: 0.8, rotateY: -10, transition: { duration: 0.3, ease: "easeIn" } }
    };

    return (
        <div className=" text-gray-800 overflow-hidden">
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 md:pt-48 md:pb-24">
                <div className="absolute inset-0 bg-black to-black z-0">
                    <img
                        src={heroBgImage}
                        alt="Background Pattern"
                        className="w-full h-full object-cover opacity-20"
                    />
                </div>
                <div className="container mx-auto px-6 text-center relative z-10 text-white">
                    <motion.div
                        variants={heroVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <motion.h1
                            className="text-4xl md:text-6xl font-extrabold leading-tight mb-4"
                            variants={itemVariants}
                        >
                            <span className="text-[#AEEA00]">COC</span> for iPhone :
                            <br className="hidden sm:inline" /> The Future of Calling
                        </motion.h1>
                        <motion.p
                            className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-8"
                            variants={itemVariants}
                        >
                            Experience the most powerful and rewarding way to use your phone.
                        </motion.p>
                        <motion.div
                            className="flex flex-col items-center justify-center space-y-4"
                            variants={itemVariants}
                        >
                            <motion.span
                                className="text-3xl font-bold text-[#AEEA00]"
                                variants={blinkVariants}
                                animate="blinking"
                            >
                                Coming Soon
                            </motion.span>
                            <a
                                href="https://www.apple.com/app-store/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white text-[#1A237E] py-3 px-8 rounded-full font-bold shadow-lg hover:bg-gray-200 transition-colors duration-300 transform hover:scale-105"
                            >
                                <div className="flex items-center space-x-2">
                                    <FaApple className="text-2xl" />
                                    <span>Download on the App Store</span>
                                </div>
                            </a>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Interactive Phone Section */}
            <section className="py-20 md:py-24">
                <div className="container mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1A237E] mb-16">
                        See Our App in Action
                    </h2>
                    <div className="grid md:grid-cols-3 gap-12 md:gap-8 items-center">
                        {/* Left Feature List (3 features) */}
                        <div className="flex flex-col space-y-8">
                            {interactiveFeatures.filter(f => f.alignment === "left").map(feature => (
                                <div
                                    key={feature.id}
                                    onClick={() => setSelectedFeature(feature)}
                                    className={`p-6 rounded-lg cursor-pointer transition-all duration-300 transform ${selectedFeature.id === feature.id ? 'bg-[#AEEA00] text-[#1A237E] shadow-xl scale-105' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                                >
                                    <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                                    <p className="text-sm">{feature.description}</p>
                                </div>
                            ))}
                        </div>

                        {/* Central Phone Display */}
                        <div className="flex justify-center items-center">
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={selectedFeature.id}
                                    src={selectedFeature.image}
                                    alt={selectedFeature.title}
                                    variants={imageVariants}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    className="w-full max-w-[15rem] md:max-w-[20rem] rounded-3xl shadow-2xl"
                                />
                            </AnimatePresence>
                        </div>

                        {/* Right Feature List (3 features) */}
                        <div className="flex flex-col space-y-8">
                            {interactiveFeatures.filter(f => f.alignment === "right").map(feature => (
                                <div
                                    key={feature.id}
                                    onClick={() => setSelectedFeature(feature)}
                                    className={`p-6 rounded-lg cursor-pointer transition-all duration-300 transform ${selectedFeature.id === feature.id ? 'bg-[#AEEA00] text-[#1A237E] shadow-xl scale-105' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                                >
                                    <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                                    <p className="text-sm">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>


           {/* Dedicated Autoplay Video Section with Gradient Banner */}
          <h2 className="text-center text-4xl md:text-5xl font-extrabold text-[#1A237E] tracking-tight leading-snug ">
  <span className="bg-blue-900 bg-clip-text text-transparent">
    Cash On Calls Features
  </span>
</h2>

<section className="relative py-20 md:py-32  overflow-visible mt-72">
 
  {/* Video Positioned Over Banner */}
  <div className="relative z-10 -mt-72 w-full max-w-4xl mx-auto rounded-3xl shadow-2xl overflow-hidden">
    <div className="rounded-3xl shadow-2xl overflow-hidden">
      <video
        src={videoplay}
        poster="https://via.placeholder.com/1280x720.png?text=Video+Loading..."
        controls={false}
        autoPlay
        loop
        muted
        playsInline
        className="w-full h-auto"
      >
        Your browser does not support the video tag.
      </video>
    </div>
  </div>

   {/* Gradient Banner Behind Video */}
  <div className="absolute inset-0 top-20 flex justify-center z-0 ">
    <div className="w-[950%] md:w-[90%] h-[500px] md:h-100 rounded-3xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 shadow-2xl relative">
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-6 text-center mt-72">
        <h2 className="text-3xl text-gray-800 md:text-5xl font-bold mb-2">Turn Every Call into Cash</h2>
        <p className="text-base text-green-200 md:text-lg max-w-xl ">
          Experience how Cash On Calls (COC) makes your daily calling smarter and profitable — all in one simple app.
        </p>
      </div>
    </div>
  </div>
</section>


            {/* App Screenshots/Gallery Section */}
            <section className="py-20 md:py-24 mt-32">
                <div className="container mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1A237E] mb-16">
                        A Glimpse Inside the App
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8  h-[400px]">
                        <div className="rounded-2xl shadow-xl overflow-hidden transform transition-transform hover:scale-105">
                            <img src={screenshot1} alt="App Screenshot 1" className="w-full h-auto object-cover" />
                        </div>
                        <div className="rounded-2xl shadow-xl overflow-hidden transform transition-transform hover:scale-105">
                            <img src={screenshot2} alt="App Screenshot 2" className="w-full h-auto object-cover" />
                        </div>
                        <div className="rounded-2xl shadow-xl overflow-hidden transform transition-transform hover:scale-105">
                            <img src={screenshot3} alt="App Screenshot 3" className="w-full h-auto object-cover" />
                        </div>
                        <div className="rounded-2xl shadow-xl overflow-hidden transform transition-transform hover:scale-105">
                            <img src={screenshot4} alt="App Screenshot 4" className="w-full h-auto object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-24 ">
  <div className="container mx-auto px-6">
    <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1A237E] mb-12">
      Features That Redefine Your Calling Experience
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
      {/* Feature 1 */}
      <div className="flex flex-col items-center text-center">
        <div className="bg-[#AEEA00] text-[#1A237E] p-6 rounded-full mb-6">
          <FaPhoneAlt className="text-4xl" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Silence Unknown Callers</h3>
        <p className="text-gray-600">
          Automatically block unknown and spam callers. Only contacts and recent outgoing calls will ring — stay focused and safe.
        </p>
      </div>

      {/* Feature 2 */}
      <div className="flex flex-col items-center text-center">
        <div className="bg-[#AEEA00] text-[#1A237E] p-6 rounded-full mb-6">
          <FaVolumeUp className="text-4xl" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Live Voicemail</h3>
        <p className="text-gray-600">
          See voicemails transcribed in real-time while the caller is recording — decide instantly whether to answer or ignore.
        </p>
      </div>

      {/* Feature 3 */}
      <div className="flex flex-col items-center text-center">
        <div className="bg-[#AEEA00] text-[#1A237E] p-6 rounded-full mb-6">
          <FaCommentDots className="text-4xl" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Call Captioning</h3>
        <p className="text-gray-600">
          Get real-time captions during phone calls — especially helpful in noisy places or for improved accessibility.
        </p>
      </div>
    </div>
  </div>
</section>

            
            <section className="py-16 md:py-20 ">
                <div className="container mx-auto px-6 flex justify-center">
                    <div className="flex flex-col md:flex-row items-center bg-gradient-to-r from-[#3F51B5] to-[#5C6BC0] rounded-3xl overflow-hidden shadow-2xl w-full md:w-[1280px] h-[360px]">
                        <div className="w-full md:w-1/2 p-6 md:p-10 text-white">
                            <h2 className="text-2xl md:text-3xl font-extrabold leading-tight mb-4">
                                Achieve More with Cash On Calls
                            </h2>
                            <p className="text-md md:text-lg mb-6">
                                Turn every incoming call into an opportunity. Track, earn, and grow
                                using our powerful dashboard that simplifies your call-based income.
                            </p>
                            <button className="bg-white text-[#3F51B5] font-semibold px-6 py-2 rounded-full hover:bg-gray-100 transition">
                                Start Earning Now
                            </button>
                        </div>
                        <div className="w-full md:w-1/2 h-full">
                            <img
                                src="https://media.idownloadblog.com/wp-content/uploads/2022/11/Compact-and-full-call-screen-iPhone-1200x675.jpg"
                                alt="iPhone Dashboard"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="relative py-16 md:py-20 overflow-hidden">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A237E] mb-4">
                        Trusted by Thousands of Callers
                    </h2>
                    <p className="text-gray-600 max-w-2xl mx-auto mb-12">
                        Join 10,000+ users who are earning rewards and income just by receiving calls. Start your journey with Cash On Calls today.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                        <div className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition duration-300">
                            <h3 className="text-3xl font-bold text-indigo-700">0K+</h3>
                            <p className="mt-2 text-gray-600 text-sm">Happy Call Earners</p>
                        </div>
                        <div className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition duration-300">
                            <h3 className="text-3xl font-bold text-indigo-700">0.0★</h3>
                            <p className="mt-2 text-gray-600 text-sm">App Store Rating</p>
                        </div>
                        <div className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition duration-300">
                            <h3 className="text-3xl font-bold text-indigo-700">₹0 Cr+</h3>
                            <p className="mt-2 text-gray-600 text-sm">Rewards Distributed</p>
                        </div>
                        <div className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition duration-300">
                            <h3 className="text-3xl font-bold text-indigo-700">0%</h3>
                            <p className="mt-2 text-gray-600 text-sm">Secure & Transparent</p>
                        </div>
                    </div>
                    <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 w-[400px] h-[400px] bg-indigo-100 rounded-full opacity-30 blur-3xl pointer-events-none"></div>
                </div>
            </section>
        </div>
    );
};

export default IphonePage;